import React, { useCallback, useMemo, useRef } from 'react';
import type { WorkOrder } from '#/graphql/types';
import type { FormAction, FormItem, FormRefType } from '#/common/components/Form';
import Form from '#/common/components/Form';
import useToast from '#/common/hooks/useToast';
import { useNavigate } from 'react-router-dom';
import type { FieldDataChangedEvent } from 'devextreme/ui/form';
import { InitializedEventInfo } from 'devextreme/events';
import dxForm from 'devextreme/ui/form';
import { states } from '../../mappings/mappings';
import { SubmitWorkOrder } from '../../utils/types';
import AttachmentsUpload from '#/common/components/AttachmentUpload';
import allFields from '../../utils/fields';
import { getCleanFormErrors } from '#/common/utils/customRelatedItemsValidation';
import {
  getItemOptions,
  setEditorOptions,
  setItemOptions,
} from '#/common/components/Form/formItemOption';
import { getHistoryFieldNames } from '#/common/utils/fields/historySection';
import { useRedirectToRelatedFindingConfirm } from '../../hooks/useRedirectToRelatedFindingsConfirm';
import { extractFieldNames } from '#/common/utils/extractFieldNames';

export interface WorkOrderFormProps {
  workOrder: WorkOrder;
  fields: FormItem<WorkOrder>[];
  title: string;
  submit?: SubmitWorkOrder;
  deleteWorkOrder?: () => unknown;
}

const WorkOrderForm: React.FC<WorkOrderFormProps> = (props) => {
  const { workOrder, fields, title, submit, deleteWorkOrder } = props;
  const { alert, confirm } = useToast();
  const navigate = useNavigate();
  const formRef = useRef<FormRefType | null>(null);
  const isArchived = !!workOrder?.isArchived;
  const isDeletable = workOrder.state === states.open.key && !isArchived;
  const isEditable = workOrder.state !== states.closed.key;
  const isNewWorkOrder = !workOrder.id;

  const { redirectToRelatedFindingConfirm } = useRedirectToRelatedFindingConfirm();
  const { findingId } = workOrder;
  const filteredFields = useMemo(() => {
    return fields.map((section) => {
      if (!('items' in section) || !Array.isArray(section.items)) {
        return section;
      }

      return {
        ...section,
        items: section.items
          .filter((item) => findingId || !('dataField' in item) || item.dataField !== 'findingId')
          .map((item) => {
            if (!('dataField' in item)) {
              return item;
            }

            if (item.dataField === 'assetId') {
              return { ...item, colSpan: 2 };
            }

            return item;
          }),
      };
    });
  }, [fields, findingId]);

  const actions = useMemo(() => {
    const result: FormAction<WorkOrder>[] = [];

    if (deleteWorkOrder && !isArchived) {
      result.push({
        label: 'Delete',
        icon: 'trash',
        type: 'danger',
        disabled: !isDeletable,
        onClick: async (e) => {
          if (!e.record) {
            return;
          }

          const sure = await confirm(
            'error',
            `Are you sure you want to delete work order ${e.record.displayNumber}?`,
          );

          if (!sure) {
            return;
          }

          e.setIsLoading(true);
          await deleteWorkOrder();
          e.setIsLoading(false);

          navigate('/work-orders');
        },
      });
    }

    if (submit && isNewWorkOrder && !isArchived) {
      result.push({
        label: 'Cancel',
        type: 'secondary',
        onClick: async (e) => {
          navigate(`/work-orders`);
        },
      });
    }

    if (submit) {
      result.push({
        label: isArchived ? 'Archived' : 'Submit',
        type: 'submit',
        disabled: (!isNewWorkOrder && !isEditable) || isArchived,
        onClick: async (e) => {
          if (!e.record) {
            return;
          }

          const validation = e.form?.validate();

          if (!validation?.isValid && getCleanFormErrors(validation, allFields, e.record).length) {
            void alert('error', `${title} is not valid, check field entries.`);
            return;
          }

          try {
            e.setIsLoading(true);
            const result = await submit(e.record);
            e.setIsLoading(false);

            if (result?.errors) {
              void alert('error', result.errors.join(`;\n`));
              return;
            }

            if (isNewWorkOrder && result?.data && result?.data.id) {
              navigate(`/work-orders/${result.data?.id}`, { replace: true });
            }
            if (
              workOrder.state !== states.closed.key &&
              e.record.state === states.closed.key &&
              !!workOrder.findingId
            ) {
              await redirectToRelatedFindingConfirm(workOrder.findingId);
            } else {
              void alert('success', 'Changes saved!');
            }
          } catch (err: any) {
            e.setIsLoading(false);
            console.log('Error', err);
            void alert('error', 'Missing or invalid data!');
          }
        },
      });
    }

    return result;
  }, [
    alert,
    confirm,
    deleteWorkOrder,
    navigate,
    submit,
    isNewWorkOrder,
    isArchived,
    isDeletable,
    isEditable,
    title,
    workOrder.state,
    workOrder.findingId,
    redirectToRelatedFindingConfirm,
  ]);

  const historyFieldNames = getHistoryFieldNames();

  // Get all field names from fields configuration - single source of truth
  const allFieldNames = useMemo(() => {
    return extractFieldNames(filteredFields, historyFieldNames);
  }, [filteredFields, historyFieldNames]);

  // Get the fields that should always be read-only as per editorOptions configuration
  const alwaysReadOnlyFields = useMemo(() => {
    const readOnlyFields: string[] = [];

    const extractReadOnlyFields = (items: FormItem<WorkOrder>[]): void => {
      items.forEach((item) => {
        // If item has a dataField directly
        if ('dataField' in item && item.dataField && !historyFieldNames.includes(item.dataField)) {
          // If item is set as read-only
          if (
            'editorOptions' in item &&
            item.editorOptions &&
            'readOnly' in item.editorOptions &&
            item.editorOptions.readOnly === true
          ) {
            readOnlyFields.push(item.dataField);
          }
        }

        // If item has nested items, recursively extract from them
        if ('items' in item && Array.isArray(item.items)) {
          extractReadOnlyFields(item.items);
        }
      });
    };

    extractReadOnlyFields(fields);

    return readOnlyFields;
  }, [fields, historyFieldNames]);

  // Set initial read-only state based on work order state (only runs once when form loads)
  const setInitialReadOnlyState = useCallback(
    (form: dxForm, state: string) => {
      const setFieldReadOnlyValue = (field: string, readOnly: boolean) => {
        if (!alwaysReadOnlyFields.includes(field)) {
          setEditorOptions(form, field, { readOnly });
        }
      };

      if (state === states.open.key || state === states.in_progress.key) {
        // open and in_progress: every field is editable
        allFieldNames.forEach((fieldName) => {
          if (isNewWorkOrder && state === states.open.key && fieldName === 'state') {
            setFieldReadOnlyValue('state', true);
          } else {
            setFieldReadOnlyValue(fieldName, false);
          }
        });
      } else if (state === states.pending_review.key) {
        // pending_review: all fields read-only except State field
        allFieldNames.forEach((fieldName) => {
          if (fieldName !== 'state' && fieldName !== 'closedAt' && fieldName !== 'closedById') {
            setFieldReadOnlyValue(fieldName, true);
          }
        });

        setFieldReadOnlyValue('state', false);
        setFieldReadOnlyValue('closedAt', false);
        setFieldReadOnlyValue('closedById', false);
      } else if (state === states.closed.key) {
        // closed: every field is read-only
        allFieldNames.forEach((fieldName) => {
          setFieldReadOnlyValue(fieldName, true);
        });
      }
    },
    [allFieldNames, alwaysReadOnlyFields, isNewWorkOrder],
  );

  const setRequiredFieldsByState = useCallback((form: dxForm, state: string) => {
    const isInProgress = state === states.in_progress.key;
    const isPendingReview = state === states.pending_review.key;
    const isClosed = state === states.closed.key;

    // in_progress State: assignedTo becomes mandatory
    const requiresInProgressFields = isInProgress || isPendingReview || isClosed;
    setItemOptions(form, 'assignedToId', 'isRequired', requiresInProgressFields);

    // pending_review State: startDate, endDate, closedNotes, actualEffort become mandatory
    // (also required for closed state)
    const requiresPendingReviewFields = isPendingReview || isClosed;
    setItemOptions(form, 'startDate', 'isRequired', requiresPendingReviewFields);
    setItemOptions(
      form,
      'startDate',
      'validationRules',
      requiresPendingReviewFields ? [{ type: 'required', message: '' }] : [],
    );

    setItemOptions(form, 'endDate', 'isRequired', requiresPendingReviewFields);
    setItemOptions(
      form,
      'endDate',
      'validationRules',
      requiresPendingReviewFields ? [{ type: 'required', message: '' }] : [],
    );

    setItemOptions(form, 'closedNotes', 'isRequired', requiresPendingReviewFields);
    setItemOptions(form, 'actualEffort', 'isRequired', requiresPendingReviewFields);

    // closed State: closedAt, closedById become mandatory
    setItemOptions(form, 'closedAt', 'isRequired', isClosed);
    setItemOptions(form, 'closedById', 'isRequired', isClosed);
  }, []);

  const setDateMinMax = (
    form: dxForm,
    currentDateField: string,
    relatedDateField: string,
    minMax: 'min' | 'max',
  ) => {
    const currentDate = form.getEditor(currentDateField)?.option('value');
    const currentDateAsDate = currentDate instanceof Date ? currentDate : new Date(currentDate);
    currentDateAsDate.setHours(0, 0, 0, 0);
    const isCurrentDateValid = currentDate && !isNaN(currentDateAsDate.getTime());

    if (currentDate === 'string' && !currentDate.includes('T')) {
      form.updateData(currentDateField, currentDateAsDate);
    }
    form.getEditor(relatedDateField)?.option(minMax, isCurrentDateValid ? currentDateAsDate : null);
  };

  const setInitialFieldOptions = useCallback(
    (form: dxForm) => {
      setDateMinMax(form, 'startDate', 'endDate', 'min');
      setDateMinMax(form, 'endDate', 'startDate', 'max');
      setDateMinMax(form, 'plannedStartDate', 'plannedEndDate', 'min');
      setDateMinMax(form, 'plannedEndDate', 'plannedStartDate', 'max');
    },
    [setDateMinMax],
  );

  const onContentReady = useCallback(
    (e: InitializedEventInfo<dxForm>) => {
      const { component: form } = e;
      if (!form) return;

      // Set field options based on data (always run for date constraints)
      setInitialFieldOptions(form);

      const formData = form.option('formData');
      setEditorOptions(form, 'assignedToId', {
        dataStoreOptions: {
          ...getItemOptions(form, 'assignedToId')?.editorOptions?.dataStoreOptions,
          accessTo: formData?.assetId ? { resource: 'assets', ids: [formData.assetId] } : undefined,
        },
      });
    },
    [setInitialFieldOptions],
  );

  const handleFormInit = useCallback(
    (e: InitializedEventInfo<dxForm>) => {
      const { component: form } = e;

      if (!form) {
        return;
      }

      const formData = form.option('formData');

      // Set required fields based on current state
      setRequiredFieldsByState(form, formData?.state);

      // Set read-only fields based on current state
      setInitialReadOnlyState(form, formData?.state);
    },
    [setRequiredFieldsByState, setInitialReadOnlyState],
  );

  const onFieldDataChanged = useCallback(
    (e: FieldDataChangedEvent) => {
      const { component: form, dataField: field, value } = e;

      // Handle state changes to set required fields
      if (field === 'state') {
        setRequiredFieldsByState(form, value);
      }

      if (field === 'startDate' || field === 'endDate') {
        const startDate = form.getEditor('startDate')?.option('value');
        const endDate = form.getEditor('endDate')?.option('value');

        const startDateAsDate = startDate instanceof Date ? startDate : new Date(startDate);
        const endDateAsDate = endDate instanceof Date ? endDate : new Date(endDate);
        startDateAsDate.setHours(0, 0, 0, 0);
        endDateAsDate.setHours(0, 0, 0, 0);

        const isStartDateValid = startDate && !isNaN(startDateAsDate.getTime());
        const isEndDateValid = endDate && !isNaN(endDateAsDate.getTime());

        if (
          startDate &&
          endDate &&
          startDateAsDate &&
          endDateAsDate &&
          isStartDateValid &&
          isEndDateValid
        ) {
          const startDateTimeZoneOffsetInMinutes = startDateAsDate.getTimezoneOffset();
          const startDateTimeZoneOffsetInSeconds = startDateTimeZoneOffsetInMinutes * 60;
          const startDateTimeZoneOffsetInMilliseconds = startDateTimeZoneOffsetInSeconds * 1000;

          const endDateTimeZoneOffsetInMinutes = endDateAsDate.getTimezoneOffset();
          const endDateTimeZoneOffsetInSeconds = endDateTimeZoneOffsetInMinutes * 60;
          const endDateTimeZoneOffsetInMilliseconds = endDateTimeZoneOffsetInSeconds * 1000;

          const millisecondsBetweenStartAndEnd =
            endDateAsDate.getTime() -
            endDateTimeZoneOffsetInMilliseconds -
            startDateAsDate.getTime() +
            startDateTimeZoneOffsetInMilliseconds;

          const secondsBetweenStartAndEnd = millisecondsBetweenStartAndEnd / 1000;
          const minutesBetweenStartAndEnd = secondsBetweenStartAndEnd / 60;
          const hoursBetweenStartAndEnd = minutesBetweenStartAndEnd / 60;
          const durationInDays = hoursBetweenStartAndEnd / 24;

          // If start date is after end date, it will show 0 (it shouldn't happen)
          form.updateData('duration', durationInDays);
        } else {
          const currentDuration = form.getEditor('duration')?.option('value');

          if (currentDuration !== null) form.updateData('duration', null);
        }
      }

      if (field === 'startDate') {
        setDateMinMax(form, 'startDate', 'endDate', 'min');
      }

      if (field === 'endDate') {
        setDateMinMax(form, 'endDate', 'startDate', 'max');
      }
      if (field === 'plannedStartDate') {
        setDateMinMax(form, 'plannedStartDate', 'plannedEndDate', 'min');
      }
      if (field === 'plannedEndDate') {
        setDateMinMax(form, 'plannedEndDate', 'plannedStartDate', 'max');
      }

      if (field === 'assetId') {
        form.updateData({ assignedToId: null });
        setEditorOptions(form, 'assignedToId', {
          dataStoreOptions: {
            ...getItemOptions(form, 'assignedToId')?.editorOptions?.dataStoreOptions,
            accessTo: value ? { resource: 'assets', ids: [value] } : undefined,
          },
        });
      }
    },
    [setRequiredFieldsByState],
  );

  return (
    <>
      <Form
        ref={formRef}
        fields={filteredFields}
        record={workOrder}
        actions={actions}
        onFieldDataChanged={onFieldDataChanged}
        onInitialized={handleFormInit}
        onContentReady={onContentReady}
      />
      {workOrder.id && (
        <AttachmentsUpload
          relatedTable="work_orders"
          disableUpload={isArchived}
          relatedItem={workOrder.id}
        />
      )}
    </>
  );
};

export default React.memo(WorkOrderForm);
